// react-magnetic-di - temporary comment, for auto dependency injection in tests
import { useEffect, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { fireOperationalEvent } from '@atlassian/ui-modifications-analytics';

import { useAdjustmentsContextActions } from '../../../../../controllers/adjustments-context';
import { useViewType } from '../../../../../controllers/issue-adjustments/hooks/use-view-type';
import { useIssueAdjustmentsActions } from '../../../../../controllers/issue-adjustments/main';
import type { ForgeExtension } from '../../../../../types';
import type { UiModificationsData } from '../../../types';

import { MAX_NUMBER_OF_UIM_APPS } from './constants';
import { type AppToRenderResult } from './types';
import { getConfiguredApps } from './utils/get-configured-apps';

interface UseAppsToRenderPayload {
	data: UiModificationsData;
	executionContextKey: string;
}

/**
 * Matches UIM configs and forge extensions required to render apps.
 * Limtis the amount of apps to render to MAX_NUMBER_OF_UIM_APPS.
 */
export const useAppsToRender = (payload: UseAppsToRenderPayload): AppToRenderResult[] | [] => {
	const { data, executionContextKey } = payload;

	const { startFieldsInitialisationTimeout } = useIssueAdjustmentsActions();
	const { setAdjustmentsAreEnabled, setHasFinishedLoading } = useAdjustmentsContextActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const viewType = useViewType();

	const configuredApps = useMemo(
		() =>
			getConfiguredApps(
				data.jira?.forge?.uiModificationsExtensions as ForgeExtension[],
				data.jira?.uiModifications,
			),
		[data.jira?.forge?.uiModificationsExtensions, data.jira?.uiModifications],
	);

	const appsToRender: AppToRenderResult[] = useMemo(() => {
		return configuredApps.slice(0, MAX_NUMBER_OF_UIM_APPS);
	}, [configuredApps]);

	useEffect(() => {
		if (data.jira?.uiModifications !== undefined) {
			setHasFinishedLoading(true);
		}
	}, [setHasFinishedLoading, data.jira?.uiModifications]);

	useEffect(() => {
		if (appsToRender.length > 0) {
			setAdjustmentsAreEnabled(true);
			startFieldsInitialisationTimeout({ createAnalyticsEvent });
		}
	}, [
		appsToRender,
		setAdjustmentsAreEnabled,
		startFieldsInitialisationTimeout,
		createAnalyticsEvent,
	]);

	useEffect(() => {
		const configuredAppsCount = configuredApps.length;

		if (configuredAppsCount >= 1) {
			fireOperationalEvent(createAnalyticsEvent, {
				action: 'success',
				actionSubject: 'jiraUiModifications.configuredAppsPerContext',
				attributes: {
					forgeModulesCount: data.jira?.forge?.uiModificationsExtensions.length,
					uimConfiguredAppCount: data.jira?.uiModifications.length,
					configuredAndInstalledApps: configuredAppsCount,
					areMultipleAppsConfiguredAndInstalled: configuredAppsCount > 1,
					viewType,
				},
			});
		}
	}, [
		// `executionContextKey` is required to track events when execution context changed
		executionContextKey,
		viewType,
		createAnalyticsEvent,
		configuredApps.length,
		data.jira?.forge?.uiModificationsExtensions.length,
		data.jira?.uiModifications.length,
	]);

	return appsToRender;
};
